// The help centre's search bar: results as you type (keyboard: ↑/↓ to move, Enter to open, Esc to close), Enter or the
// button for every match (/help?q=…), and a filters button INSIDE the bar at its end (owner, 29 Oct 2026) with section,
// category and the content language. Filters live in the URL (?section=, ?category=, ?lang=) so a filtered search can be
// shared; on the hub they apply live. Phones get the filters as a bottom sheet, wider screens a popover.
//
// The input is 16px at every size so iOS Safari doesn't zoom in when it's focused (user zoom stays allowed).
import { useId, useMemo, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, SlidersHorizontal } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { HELP_CATEGORIES, HELP_LANGS, HELP_PATH, HELP_SECTIONS, helpArticlePath, hubSections, isSeparateSection, label, type HelpSectionId } from "@/content/help";
import { highlightParts, queryTerms, searchHelp, type HelpIndexEntry } from "@/content/help/search";
import { LANG_PARAM, type SeoLang } from "@/lib/seo/site";
import { keptParams, useHelpContentVersion } from "./helpLinks";

/** The languages' own names (the same in every UI language). */
export const HELP_LANG_LABELS: Partial<Record<SeoLang, string>> = {
  en: "English",
  es: "Español",
  pt: "Português",
  de: "Deutsch",
  fr: "Français",
  it: "Italiano",
  ja: "日本語",
  zh: "中文",
  ms: "Bahasa Melayu",
};

export interface HelpFilters {
  section?: HelpSectionId;
  category?: string;
}

/** The filters a URL asks for (unknown values are ignored; a category must belong to the section). */
export const readHelpFilters = (search: string): HelpFilters => {
  const p = new URLSearchParams(search);
  const section = HELP_SECTIONS.find((s) => s.id === p.get("section"))?.id;
  const cat = HELP_CATEGORIES.find((c) => c.id === p.get("category"));
  const category = cat && (!section || cat.section === section) ? cat.id : undefined;
  return { section: section ?? cat?.section, category };
};

/** /help with the query and filters (and the kept ?lang= / ?ws=). */
export const helpSearchUrl = (search: string, next: { q?: string; section?: string; category?: string; lang?: string }): string => {
  const p = new URLSearchParams(keptParams(search));
  if (next.lang !== undefined) {
    if (next.lang && next.lang !== "en") p.set(LANG_PARAM, next.lang);
    else p.delete(LANG_PARAM);
  }
  for (const k of ["q", "section", "category"] as const) {
    const v = next[k];
    if (v) p.set(k, v);
    else p.delete(k);
  }
  const qs = p.toString();
  return `${HELP_PATH}${qs ? `?${qs}` : ""}`;
};

/** The matched words of `text`, wrapped in <mark>. */
export const Highlight = ({ text, terms }: { text: string; terms: string[] }) => (
  <>
    {highlightParts(text, terms).map((p, i) =>
      p.hit ? (
        <mark key={i} className="rounded-sm bg-hawk-orange/20 text-inherit">
          {p.text}
        </mark>
      ) : (
        <span key={i}>{p.text}</span>
      ),
    )}
  </>
);

const Field = ({ id, label: text, children }: { id: string; label: string; children: ReactNode }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-sm font-semibold text-foreground">
      {text}
    </label>
    {children}
  </div>
);

const selectCls = "h-11 w-full rounded-lg border border-border bg-background px-3 text-base text-foreground";

/** The filters' controls (popover or bottom sheet). Changes apply at once. */
const FilterControls = ({ lang, filters, onChange, onClear, onDone }: { lang: SeoLang; filters: HelpFilters; onChange: (next: { section?: string; category?: string; lang?: string }) => void; onClear: () => void; onDone: () => void }) => {
  const { t } = useTranslation();
  const id = useId();
  // A separate section (the Photohawk app's help) filters only within itself; the hub's filters never offer it.
  const separate = isSeparateSection(filters.section);
  const sections = separate ? HELP_SECTIONS.filter((s) => s.id === filters.section) : hubSections();
  const cats = HELP_CATEGORIES.filter((c) => (filters.section ? c.section === filters.section : !isSeparateSection(c.section)));
  return (
    <div className="space-y-4" data-testid="help-filters">
      <Field id={`${id}-s`} label={t("help.search.filterSection")}>
        <select
          id={`${id}-s`}
          className={selectCls}
          value={filters.section ?? ""}
          onChange={(e) => onChange({ section: e.target.value || undefined, category: undefined })}
        >
          {!separate && <option value="">{t("help.search.allSections")}</option>}
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {t(`help.sections.${s.id}.title`)}
            </option>
          ))}
        </select>
      </Field>
      <Field id={`${id}-c`} label={t("help.search.filterCategory")}>
        <select
          id={`${id}-c`}
          className={selectCls}
          value={filters.category ?? ""}
          onChange={(e) => {
            const c = HELP_CATEGORIES.find((x) => x.id === e.target.value);
            onChange({ category: c?.id, section: c?.section ?? filters.section });
          }}
        >
          <option value="">{t("help.search.allCategories")}</option>
          {cats.map((c) => (
            <option key={c.id} value={c.id}>
              {label(c.title, lang)}
            </option>
          ))}
        </select>
      </Field>
      <Field id={`${id}-l`} label={t("help.search.filterLanguage")}>
        <select id={`${id}-l`} className={selectCls} value={lang} onChange={(e) => onChange({ lang: e.target.value })}>
          {HELP_LANGS.map((l) => (
            <option key={l} value={l}>
              {HELP_LANG_LABELS[l] ?? l}
            </option>
          ))}
        </select>
      </Field>
      <div className="flex items-center justify-between gap-2 pt-1">
        <button type="button" onClick={onClear} className="min-h-11 rounded-lg px-3 text-sm font-semibold text-[hsl(18_100%_38%)] hover:underline">
          {t("help.search.clearFilters")}
        </button>
        <button type="button" onClick={onDone} className="min-h-11 rounded-lg bg-gradient-to-r from-hawk-orange to-hawk-gold px-5 text-sm font-semibold text-white hover:opacity-95">
          {t("help.search.done")}
        </button>
      </div>
    </div>
  );
};

/**
 * The search box. `index` is the help search index. On the hub (`live`), the filters change the page's results at once;
 * elsewhere they open the hub's results with those filters.
 */
export const HelpSearchBox = ({ index, lang, initial, onHub = false, scope }: { index: () => HelpIndexEntry[]; lang: SeoLang; initial?: string; onHub?: boolean; scope?: HelpSectionId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const isMobile = useIsMobile();
  const listId = useId();
  // `scope`: a separate section's own pages search only that section (its results open the hub's results page with
  // ?section=, which keeps it).
  const read = readHelpFilters(search);
  const filters: HelpFilters = scope && read.section !== scope ? { section: scope } : read;
  // The query starts as the page's own (?q=) unless the caller gives one.
  const [q, setQ] = useState(initial ?? new URLSearchParams(search).get("q") ?? "");
  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(-1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const version = useHelpContentVersion();
  const hits = useMemo(
    () => (q.trim().length >= 2 ? searchHelp(index(), q, lang, { limit: 6, section: filters.section, category: filters.category }) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` rebuilds the index when a language loads
    [q, lang, filters.section, filters.category, index, version],
  );
  const terms = useMemo(() => queryTerms(q), [q]);
  // A separate section isn't a filter the visitor chose: it's where they are.
  const activeCount = (filters.section && !isSeparateSection(filters.section) ? 1 : 0) + (filters.category ? 1 : 0);
  const showList = open && hits.length > 0;

  const go = (next: { q?: string; section?: string; category?: string; lang?: string }, replace = false) =>
    navigate(helpSearchUrl(search, { q: q.trim(), section: filters.section, category: filters.category, ...next }), { replace });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (showList && active >= 0 && hits[active]) {
      navigate(`${helpArticlePath(hits[active].slug)}${keptParams(search)}`);
      return;
    }
    setOpen(false);
    go({ q: q.trim() });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && hits.length) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % hits.length);
    } else if (e.key === "ArrowUp" && hits.length) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i <= 0 ? hits.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      if (showList) {
        e.preventDefault();
        setOpen(false);
        setActive(-1);
      }
    }
  };

  // Filters: on the hub they apply to the page at once (the URL is replaced, so Back leaves the page); elsewhere a
  // change opens the hub's results.
  const changeFilters = (next: { section?: string; category?: string; lang?: string }) => go(next, onHub);
  const clearFilters = () => go(isSeparateSection(filters.section) ? { section: filters.section, category: undefined } : { section: undefined, category: undefined }, onHub);
  const controls = (
    <FilterControls lang={lang} filters={filters} onChange={changeFilters} onClear={clearFilters} onDone={() => setFiltersOpen(false)} />
  );
  const filterButton = (
    <button
      type="button"
      aria-label={activeCount ? `${t("help.search.filters")} (${activeCount})` : t("help.search.filters")}
      title={t("help.search.filters")}
      aria-haspopup="dialog"
      aria-expanded={filtersOpen}
      onClick={() => setFiltersOpen((v) => !v)}
      className="relative mr-1 flex h-10 w-10 flex-none items-center justify-center rounded-lg text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
      data-testid="help-filters-button"
    >
      <SlidersHorizontal size={20} aria-hidden="true" />
      {activeCount > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-hawk-orange px-1 text-[11px] font-bold text-white" aria-hidden="true">
          {activeCount}
        </span>
      )}
    </button>
  );

  return (
    <form role="search" onSubmit={submit} className="relative max-w-2xl" data-testid="help-search">
      <label htmlFor="help-q" className="sr-only">
        {t("help.search.label")}
      </label>
      <div className="flex items-center gap-1 rounded-xl bg-white p-1.5 shadow-lg">
        <Search size={20} className="ml-2 flex-none text-zinc-500" aria-hidden="true" />
        <input
          id="help-q"
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={onKeyDown}
          placeholder={t(filters.section === "app" ? "help.search.appPlaceholder" : "help.search.placeholder")}
          autoComplete="off"
          // 16px at every size: iOS Safari zooms into inputs smaller than that.
          style={{ fontSize: "max(16px, 1rem)" }}
          className="min-h-11 w-full min-w-0 flex-1 bg-transparent px-1 text-zinc-900 outline-none placeholder:text-zinc-500"
        />
        {isMobile ? (
          <>
            {filterButton}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetContent side="bottom" className="rounded-t-2xl">
                <SheetHeader className="mb-4">
                  <SheetTitle>{t("help.search.filters")}</SheetTitle>
                </SheetHeader>
                {controls}
              </SheetContent>
            </Sheet>
          </>
        ) : (
          <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
            <PopoverTrigger asChild>{filterButton}</PopoverTrigger>
            <PopoverContent align="end" className="w-80" aria-label={t("help.search.filters")}>
              {controls}
            </PopoverContent>
          </Popover>
        )}
        <button type="submit" className="min-h-11 flex-none rounded-lg bg-gradient-to-r from-hawk-orange to-hawk-gold px-4 font-semibold text-white hover:opacity-95">
          {t("help.search.button")}
        </button>
      </div>
      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t("help.search.label")}
          className="absolute left-0 right-0 top-full z-20 m-0 mt-2 list-none overflow-hidden rounded-xl border border-border bg-card p-0 text-left text-foreground shadow-2xl"
          data-testid="help-suggestions"
        >
          {hits.map((h, i) => (
            <li key={h.slug} id={`${listId}-${i}`} role="option" aria-selected={i === active} className={`border-b border-border last:border-0 ${i === active ? "bg-secondary" : ""}`}>
              <Link to={`${helpArticlePath(h.slug)}${keptParams(search)}`} className="block px-4 py-3 hover:bg-secondary/60" onMouseEnter={() => setActive(i)}>
                <span className="block font-semibold">
                  <Highlight text={h.title} terms={terms} />
                </span>
                <span className="block truncate text-sm text-muted-foreground">
                  <Highlight text={h.description} terms={terms} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
};
